import React from 'react'
import PropTypes from 'prop-types'
import {Query as ApolloQuery} from 'react-apollo'
import {captureException} from '@sentry/browser'
import get from 'lodash.get'
import {Spin} from 'antd'
import {CloseCircleFilled} from '@ant-design/icons'
import useRaygun from 'shared/hooks/useRaygun'
import './index.css'

const config = window.ENV

// Return results from cache if any, while a new network request is in flight.
// Default behaviour, cache-first, will never ask the server if there is
// data in the cache to fulfill the query. With cache-and-network, previous
// data will be returned while up-to-date results are being queried.
const CACHE_AND_NETWORK_POLICY = 'cache-and-network'

const Query = ({
  children,
  inlineLoading,
  inlineError,
  fetchPolicy = CACHE_AND_NETWORK_POLICY,
  ...props
}) => {
  function handlePageReload(e) {
    e.preventDefault()
    window.location.reload(true)
  }

  const raygun = useRaygun()

  return (
    <ApolloQuery fetchPolicy={fetchPolicy} {...props}>
      {(result) => {
        let isLoading = result.loading
        if (fetchPolicy === CACHE_AND_NETWORK_POLICY) {
          // Do not return a loading state if policy is cache-and-network
          // and data exists in cache.
          isLoading = isLoading && Object.keys(result.data || {}).length === 0
        }

        if (isLoading && inlineLoading) {
          return (
            <div className="query-loading">
              <Spin />
            </div>
          )
        }

        if (result.error && inlineError) {
          console.log(result.error)
          // TODO: remove this when all apps are moved to Raygun
          if (config.SENTRY_DSN) {
            captureException(result.error)
          }

          if (raygun) {
            raygun('send', result.error)
          }
          return (
            <div className="query-error">
              <div className="error-content">
                <span className="error-icon">
                  <CloseCircleFilled />
                </span>
                <span className="error-text">
                  Noe gikk galt!
                </span>
                <span className="description-text">
                  En feil har oppstått og innholdet her kan ikke lastes inn.
                  Last siden på nytt og førsøk på nytt. Vennligst kontakt EFKT om
                  problemet vedvarer.
                </span>
                <span className="description-text">
                  <a href={window.location.href} onClick={handlePageReload}>
                    Klikk her for å laste siden på nytt
                  </a>
                </span>
              </div>
            </div>
          )
        }

        const getData = (prop, defaultValue = null) =>
          get(result.data, prop, defaultValue)

        return children({...result, getData})
      }}
    </ApolloQuery>
  )
}

Query.propTypes = {
  children: PropTypes.func.isRequired,
  inlineLoading: PropTypes.bool,
  inlineError: PropTypes.bool,
  fetchPolicy: PropTypes.string,
}

Query.defaultProps = {
  inlineLoading: false,
  inlineError: false,
}

export default Query
